import { create } from "zustand";
import type {
  AccountSuggestion,
  AccountBalance,
  AccountItem,
  CreateCheckpointRequest,
  CreateAccountRequest,
  UpdateAccountRequest,
} from "@boo/shared";
import {
  acceptAccountSuggestion,
  addCheckpoint,
  dismissAccountSuggestion,
  fetchAccountSuggestions,
  createAccount,
  deleteAccount,
  fetchAccounts,
  fetchBalances,
  updateAccount,
} from "../../../services/accountApi";

interface AccountState {
  accounts: AccountItem[];
  balances: Record<string, AccountBalance>;
  suggestions: AccountSuggestion[];
  accountsLoaded: boolean;
  loading: boolean;
  loadSuggestions: () => Promise<void>;
  acceptSuggestion: (
    id: string,
    payload: CreateAccountRequest,
  ) => Promise<void>;
  dismissSuggestion: (id: string, never: boolean) => Promise<void>;
  loadAccounts: () => Promise<void>;
  ensureAccounts: () => Promise<void>;
  createAccount: (payload: CreateAccountRequest) => Promise<void>;
  updateAccount: (id: string, payload: UpdateAccountRequest) => Promise<void>;
  deleteAccount: (id: string) => Promise<void>;
  addCheckpoint: (
    id: string,
    payload: CreateCheckpointRequest,
  ) => Promise<void>;
}

// Concurrent ensureAccounts() callers share one request.
let accountsRequest: Promise<void> | null = null;

export const useAccountStore = create<AccountState>((set, get) => ({
  accounts: [],
  balances: {},
  suggestions: [],
  accountsLoaded: false,
  loading: false,

  async loadAccounts() {
    set({ loading: true });
    try {
      const [accounts, balances] = await Promise.all([
        fetchAccounts(),
        fetchBalances(),
      ]);
      set({
        accounts,
        balances: Object.fromEntries(balances.map((b) => [b.accountId, b])),
        accountsLoaded: true,
      });
    } finally {
      set({ loading: false });
    }
  },

  /**
   * Accounts only (names, ids), once per session: for pages that label or pick
   * accounts but never show balances. A failure is not remembered, so the next
   * caller retries.
   */
  ensureAccounts() {
    if (get().accountsLoaded) return Promise.resolve();
    accountsRequest ??= (async () => {
      try {
        set({ accounts: await fetchAccounts(), accountsLoaded: true });
      } finally {
        accountsRequest = null;
      }
    })();
    return accountsRequest;
  },

  // Default flag moves between rows server-side, so refetch instead of patching locally.
  async createAccount(payload) {
    await createAccount(payload);
    await get().loadAccounts();
  },

  async updateAccount(id, payload) {
    await updateAccount(id, payload);
    await get().loadAccounts();
  },

  async deleteAccount(id) {
    await deleteAccount(id);
    set((s) => ({ accounts: s.accounts.filter((a) => a.id !== id) }));
  },

  async addCheckpoint(id, payload) {
    await addCheckpoint(id, payload);
    await get().loadAccounts();
  },

  async loadSuggestions() {
    set({ suggestions: await fetchAccountSuggestions() });
  },

  async acceptSuggestion(id, payload) {
    await acceptAccountSuggestion(id, payload);
    await Promise.all([get().loadAccounts(), get().loadSuggestions()]);
  },

  async dismissSuggestion(id, never) {
    await dismissAccountSuggestion(id, never);
    await get().loadSuggestions();
  },
}));

/** Suggestions only feed the nav badge, so a failed refresh is not worth surfacing. */
export async function refreshSuggestions(): Promise<void> {
  try {
    await useAccountStore.getState().loadSuggestions();
  } catch {
    // Badge keeps its last value.
  }
}
